/**
 * RemoveFromCollectionDialog - Dialog para remover artigos de coleções selecionadas
 *
 * Mostra as coleções (tipo 'articles') que contêm os artigos selecionados
 * e permite escolher de quais coleções remover
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  Collections as CollectionIcon,
  RemoveCircleOutline as RemoveIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const RemoveFromCollectionDialog = ({
  open,
  onClose,
  articleIds = [],
  onSuccess
}) => {
  const theme = useTheme();
  const [allCollections, setAllCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [totalArticles, setTotalArticles] = useState(0);

  useEffect(() => {
    if (open && articleIds.length > 0) {
      fetchCommonCollections();
      setSelectedCollections([]);
      setError(null);
      setSuccess(null);
    }
  }, [open, articleIds]);

  const fetchCommonCollections = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.post('/collections/common-collections', {
        articleIds
      });

      if (res.data.success) {
        setAllCollections(res.data.allCollections || []);
        setTotalArticles(res.data.totalArticles || articleIds.length);

        if (res.data.allCollections.length === 0) {
          setError('Nenhuma coleção encontrada com os artigos selecionados');
        }
      } else {
        setError(res.data.error || 'Erro ao buscar coleções');
      }
    } catch (err) {
      console.error('Erro ao buscar coleções dos artigos:', err);
      setError(err.response?.data?.error || 'Erro ao buscar coleções dos artigos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCollection = (collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      }
      return [...prev, collectionId];
    });
  };

  const handleSelectAll = () => {
    if (selectedCollections.length === allCollections.length) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(allCollections.map(c => c._id));
    }
  };

  const handleRemoveFromCollections = async () => {
    if (selectedCollections.length === 0) return;

    try {
      setSaving(true);
      setError(null);

      const res = await api.post('/collections/bulk-remove-articles', {
        articleIds,
        collectionIds: selectedCollections
      });

      if (res.data.success) {
        setSuccess(`Artigos removidos de ${res.data.modifiedCount} coleções`);
        setTimeout(() => {
          onSuccess?.(res.data.modifiedCount);
          onClose();
        }, 1500);
      } else {
        setError(res.data.error || 'Erro ao remover de coleções');
      }
    } catch (err) {
      console.error('Erro ao remover de coleções:', err);
      setError(err.response?.data?.error || 'Erro ao remover de coleções');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <PlatformDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <PlatformDialogTitle
          title="Remover de Coleções"
          subtitle={`${articleIds.length} artigo(s) selecionado(s)`}
          icon={<RemoveIcon />}
          onClose={handleClose}
          sx={{
            '& .MuiBox-root:first-of-type': {
              background: (theme) => theme.palette.error.main,
              color: 'white'
            }
          }}
      />

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Buscando coleções dos artigos...
            </Typography>
          </Box>
        ) : error && allCollections.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : success ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <SuccessIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main">
              {success}
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selecione as coleções de onde deseja remover os artigos selecionados.
              </Typography>
            </Box>

            {allCollections.length > 0 && (
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  startIcon={<Checkbox
                    checked={selectedCollections.length === allCollections.length}
                    indeterminate={selectedCollections.length > 0 && selectedCollections.length < allCollections.length}
                    size="small"
                  />}
                >
                  {selectedCollections.length === allCollections.length ? 'Desmarcar todas' : 'Selecionar todas'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {selectedCollections.length} selecionadas
                </Typography>
              </Box>
            )}

            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {allCollections.map((collection) => (
                <ListItem
                  key={collection._id}
                  disablePadding
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: selectedCollections.includes(collection._id)
                      ? `${theme.palette.error.main}10`
                      : 'transparent',
                    '&:hover': {
                      bgcolor: `${theme.palette.error.main}08`
                    }
                  }}
                >
                  <ListItemButton onClick={() => handleToggleCollection(collection._id)} dense>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedCollections.includes(collection._id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                        color="error"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CollectionIcon fontSize="small" sx={{ color: 'primary.main' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {collection.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {collection.count} de {totalArticles} artigo(s) selecionados
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          {success ? 'Fechar' : 'Cancelar'}
        </Button>
        {!success && allCollections.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveFromCollections}
            disabled={saving || selectedCollections.length === 0}
            startIcon={saving ? <CircularProgress size={16} /> : <RemoveIcon />}
          >
            {saving ? 'Removendo...' : `Remover (${selectedCollections.length})`}
          </Button>
        )}
      </DialogActions>

      {saving && <LinearProgress color="error" />}
    </PlatformDialog>
  );
};

export default RemoveFromCollectionDialog;

